import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, type ReactNode } from "react";

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Tiempo de vida de los datos en caché (5 minutos)
            staleTime: 5 * 60 * 1000,
            // Tiempo de vida en caché (10 minutos)
            gcTime: 10 * 60 * 1000,
            // Reintentos en caso de error
            retry: (failureCount, error) => {
              // No reintentar en errores 4xx (excepto 408, 429)
              if (error && typeof error === "object" && "status" in error) {
                const status = (error as { status: number }).status;
                if (
                  status >= 400 &&
                  status < 500 &&
                  status !== 408 &&
                  status !== 429
                ) {
                  return false;
                }
              }
              // Máximo 3 reintentos
              return failureCount < 3;
            },
            // Reintentar automáticamente en segundo plano
            refetchOnWindowFocus: false,
            // Reintentar cuando se reconecta la red
            refetchOnReconnect: true,
            // Reintentar cuando se monta el componente
            refetchOnMount: true,
          },
          mutations: {
            // Reintentos para mutaciones
            retry: (failureCount, error) => {
              // No reintentar en errores 4xx (excepto 408, 429)
              if (error && typeof error === "object" && "status" in error) {
                const status = (error as { status: number }).status;
                if (
                  status >= 400 &&
                  status < 500 &&
                  status !== 408 &&
                  status !== 429
                ) {
                  return false;
                }
              }
              // Máximo 1 reintento para mutaciones
              return failureCount < 1;
            },
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* React Query DevTools solo en desarrollo */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
